/**
 * PERSONALISATION SELECTORS
 *
 * Behaviour for snippets/personalisation-selectors.liquid.
 *
 * Loaded with `defer` from inside the snippet, so it always runs after assets/custom.js (also
 * deferred, in the head) — custom.js's top level has therefore already captured its references
 * to .product-engraving-js / .upload-lift-text-js and wired setupToggleField() on them. Moving
 * those elements afterwards does not invalidate those references.
 *
 * Responsibilities:
 *   1. Move the Engraving / Gift Box Message line item property blocks and the Upload Lift app
 *      block into the add-on cards.
 *   2. Translate the checkbox UI into variant option selections, via the hidden option inputs
 *      that <variant-picker> reads.
 *   3. Keep the selected/active/unavailable states, the engraving pill and the greeting preview
 *      in sync.
 *
 * The host element carries no data-block-type, so <product-rerender> never replaces it — which
 * is what lets the relocated fields keep their typed text across variant changes.
 */

if (!window.customElements.get('personalisation-selectors')) {
  class PersonalisationSelectors extends HTMLElement {
    connectedCallback() {
      this.formElement = document.forms[this.closest('variant-picker')?.getAttribute('form-id')];
      this.productForm = this.closest('.product-form');
      this.variants = this._readVariants();
      this.optionNames = this._readOptionNames();
      this.roseFinishValues = (this.getAttribute('data-rose-finish-values') || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);

      this._relocateFields();
      this._setupEngravingField();
      this._setupGreetingField();
      this._setupToggles();
      this._setupChoices();
      this._setupColourGuard();

      this.onVariantChanged = this._onVariantChanged.bind(this);
      document.addEventListener('variant:changed', this.onVariantChanged);

      this._setupPhotoButton();
      this._syncPhotoUpload();
      this._syncAvailability();
      this._patchZoom();
      this._syncGalleryMedia();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
    }

    /* ---------------------------------------------------------------- *
     * Setup
     * ---------------------------------------------------------------- */

    _readOptionNames() {
      const script = this.closest('variant-picker')?.querySelector('script[data-personalisation-variants]');

      try {
        return JSON.parse(script?.getAttribute('data-option-names') || '[]');
      } catch (error) {
        return [];
      }
    }

    _readVariants() {
      const script = this.closest('variant-picker')?.querySelector('script[data-personalisation-variants]');

      try {
        return JSON.parse(script?.textContent || '[]');
      } catch (error) {
        console.warn('[FFS] personalisation: could not parse variant data', error);
        return [];
      }
    }

    /**
     * Move the line item property blocks into the cards. Scoped to this instance's .product-form
     * so the main product form and the quick buy drawer never steal each other's fields.
     */
    _relocateFields() {
      const scope = this.productForm || document;

      const engravingSlot = this.querySelector('[data-personalisation-slot="engraving"]');
      const engravingField = scope.querySelector('.product-engraving-js');

      if (engravingSlot && engravingField && !engravingSlot.contains(engravingField)) {
        engravingSlot.appendChild(engravingField);
      }

      const greetingSlot = this.querySelector('[data-personalisation-slot="greeting"]');
      const greetingField = scope.querySelector('.upload-lift-text-js');

      if (greetingSlot && greetingField && !greetingSlot.contains(greetingField)) {
        const uploadBlock = greetingField.nextElementSibling?.classList.contains('shopify-app-block')
          ? greetingField.nextElementSibling
          : null;

        greetingSlot.appendChild(greetingField);

        // The Upload Lift block goes in its own slot so our label, button and helper line can
        // wrap it. The old adjacency requirement is gone: it only existed for renderPreview()
        // in custom.js, and the greeting preview no longer uses that canvas.
        if (uploadBlock) {
          const photoSlot = this.querySelector('[data-personalisation-slot="photo"]');

          (photoSlot || greetingSlot).appendChild(uploadBlock);
          this.uploadBlock = uploadBlock;
        }
      }
    }

    /**
     * The Upload Lift block only belongs to the "& Photo" value. custom.js's setupToggleField
     * shows the message field for every active greeting value, but it does not know about the
     * app block, so this component owns showing and hiding it.
     */
    _syncPhotoUpload(selected) {
      const photoValue = this.querySelector('[data-choice-kind="photo"]')?.getAttribute('data-value-name');
      const current =
        selected ||
        this.querySelector('[data-personalisation-choice="greeting"]:checked')?.getAttribute('data-value-name');
      const photoOn = Boolean(photoValue) && current === photoValue;

      if (this.uploadBlock) {
        this.uploadBlock.hidden = !photoOn;
      }

      const wrapper = this.querySelector('[data-personalisation-photo]');

      if (wrapper) {
        wrapper.hidden = !photoOn;
      }

      const photoArea = this.querySelector('[data-personalisation-greeting-photo]');

      if (photoArea) {
        photoArea.hidden = !photoOn;
      }
    }

    _setupEngravingField() {
      const slot = this.querySelector('[data-personalisation-slot="engraving"]');
      const field = slot?.querySelector('.product-engraving-js');
      const input = field?.querySelector('input[type="text"], textarea');

      if (!input) return;

      this._applyMaxLength(slot, input);
      this._addCounter(field, input);

      this.engravingInput = input;
      this.pill = this.querySelector('[data-personalisation-pill]');
      this.pillText = this.querySelector('[data-personalisation-pill-text]');

      // Listen on the wrapper, not the input. custom.js's handleEngravingInput() is bound to the
      // wrapper at parse time and rewrites the value through engravingLimiter(); a listener on
      // the input itself would fire first and read the untrimmed value. Same element, and we
      // register second, so this runs after the trim.
      field.addEventListener('input', () => {
        this._updateCounter(input);
        this._updatePill(input.value);
      });

      this._updatePill(input.value);

      // Fill in the help text from the resolved limit when the merchant left it blank.
      const help = this.querySelector('[data-personalisation-help="engraving"]');

      if (help && !help.textContent.trim() && input.maxLength > 0) {
        help.textContent = `Max ${input.maxLength} characters`;
      }
    }

    _setupGreetingField() {
      const slot = this.querySelector('[data-personalisation-slot="greeting"]');
      // Scope to the line item property wrapper: the Upload Lift block shares this slot and has
      // inputs and labels of its own.
      const field = slot?.querySelector('.upload-lift-text-js');
      const input = field?.querySelector('input[type="text"], textarea');

      if (!input) return;

      this._applyMaxLength(slot, input);
      this._addCounter(field, input);

      // The line item property block renders no placeholder, so apply the block's setting here.
      const placeholder = slot.getAttribute('data-field-placeholder');

      if (placeholder && !input.placeholder) {
        input.placeholder = placeholder;
      }

      this.greetingPreview = this.querySelector('[data-personalisation-greeting-preview]');
      this.greetingMessage = this.querySelector('[data-personalisation-greeting-message]');
      this.greetingMessageText = this.querySelector('[data-personalisation-greeting-message-text]');
      this.greetingInput = input;
      this.lastAcceptedMessage = input.value || '';

      // Stop the newline at the keystroke rather than inserting it and undoing it below. Same
      // outcome, but nothing ever appears in the field, so there is no flicker and no caret to
      // put back. The input guard stays as the backstop for text that reaches a fourth line by
      // wrapping rather than by Enter.
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return;
        if (this._greetingMessageLines() < this._greetingMessageMaxLines()) return;

        event.preventDefault();
      });

      input.addEventListener('input', () => {
        this._updateCounter(input);
        this._updateGreetingMessage();
      });

      this._updateGreetingMessage();
      this._watchPhotoInput();
    }

    /**
     * Mirror the typed message into the preview, falling back to the placeholder when empty, and
     * hold the message to three rendered lines.
     *
     * The cap is enforced by rendering and then measuring, rather than by counting newlines: three
     * lines has to mean three *visible* lines, and one long sentence that wraps counts for two or
     * three on its own. Newline counting would let that through.
     */
    _updateGreetingMessage() {
      if (!this.greetingMessage) return;

      const input = this.greetingInput;
      const previous = this.lastAcceptedMessage || '';
      const next = input?.value || '';

      this._renderGreetingMessage(next);

      if (!input || !this._greetingMessageOverflows()) {
        this.lastAcceptedMessage = next;
        return;
      }

      // Reject the change that tipped it onto a fourth line. Assigning .value drops the caret to
      // the end, which is jarring mid-sentence, so put it back where the rejected input happened —
      // the length delta works for a pasted block as well as a single keystroke.
      const caret = Math.max(
        0,
        Math.min(previous.length, (input.selectionStart || 0) - (next.length - previous.length))
      );

      input.value = previous;
      input.setSelectionRange(caret, caret);
      this._renderGreetingMessage(previous);
    }

    _renderGreetingMessage(value) {
      const target = this.greetingMessageText || this.greetingMessage;
      const placeholder = this.greetingPreview?.getAttribute('data-placeholder') || '';

      target.textContent = value.trim() ? value : placeholder;
      this.greetingMessage.classList.toggle('is-placeholder', !value.trim());
    }

    _greetingMessageOverflows() {
      if (!this.greetingMessageText) return false;

      return this.greetingMessageText.offsetHeight > this.greetingMessage.clientHeight;
    }

    /**
     * How many lines the preview text currently occupies, rounded to the nearest whole line.
     * Zero while the placeholder is showing — that is not the customer's message, and a long
     * placeholder would otherwise block the very first Enter on an empty field.
     */
    _greetingMessageLines() {
      if (!this.greetingMessageText) return 0;
      if (this.greetingMessage.classList.contains('is-placeholder')) return 0;

      const styles = window.getComputedStyle(this.greetingMessageText);
      const lineHeight = parseFloat(styles.lineHeight);

      if (!lineHeight) return 0;

      return Math.round(this.greetingMessageText.offsetHeight / lineHeight);
    }

    /** Read the cap off the same custom property the CSS sizes the box with, so the two agree. */
    _greetingMessageMaxLines() {
      const declared = parseInt(
        window.getComputedStyle(this.greetingMessage).getPropertyValue('--greeting-message-lines'),
        10
      );

      return declared > 0 ? declared : 3;
    }

    /**
     * Upload Lift injects and removes input[name="properties[Gift Box Picture]"] with the hosted
     * URL of the uploaded file. Reuse custom.js's observeElement() ping-pong rather than writing
     * another MutationObserver — it already watches this exact input.
     */
    _watchPhotoInput() {
      const image = this.querySelector('[data-personalisation-greeting-photo-image]');

      if (!image || typeof observeElement !== 'function') {
        this._syncPhotoImage();
        return;
      }

      const selector = 'input[name="properties[Gift Box Picture]"]';

      const watchAdded = () => {
        observeElement('div[data-upload-lift]', selector, () => {
          this._syncPhotoImage();
          watchRemoved();
        }, 'added');
      };

      const watchRemoved = () => {
        observeElement('div[data-upload-lift]', selector, () => {
          this._syncPhotoImage();
          watchAdded();
        }, 'removed');
      };

      watchAdded();
      this._syncPhotoImage();
    }

    _syncPhotoImage() {
      const image = this.querySelector('[data-personalisation-greeting-photo-image]');

      if (!image) return;

      const url = this.querySelector('input[name="properties[Gift Box Picture]"]')?.value;

      if (url) {
        image.src = url;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
    }

    /**
     * Point our styled button at Upload Lift's own file input, and only hide the app's widget
     * once we know the hand-off worked — a failed hook-up leaves its control usable rather than
     * shipping a dead button.
     */
    _setupPhotoButton() {
      const button = this.querySelector('[data-personalisation-photo-button]');
      const slot = this.querySelector('[data-personalisation-slot="photo"]');

      if (!button || !slot) return;

      const fileInput = slot.querySelector('input[type="file"]');
      const dropzone = slot.querySelector('[data-upload-lift] .filepond--drop-label, [data-upload-lift] label');

      if (!fileInput && !dropzone) {
        // Nothing to drive — leave Upload Lift's own control visible and drop our button.
        button.hidden = true;
        return;
      }

      slot.classList.add('personalisation__photo-slot--replaced');

      button.addEventListener('click', () => {
        if (fileInput) {
          fileInput.click();
        } else {
          dropzone.click();
        }
      });
    }

    /** A block level max length setting wins over the line item property's own max_length. */
    _applyMaxLength(slot, input) {
      const override = parseInt(slot.getAttribute('data-max-length'), 10);

      if (override > 0) {
        input.setAttribute('maxlength', override);

        if (input.value.length > override) {
          input.value = input.value.substring(0, override);
        }
      }
    }

    _addCounter(field, input) {
      const label = field.querySelector('label');

      if (!label || input.maxLength <= 0 || field.querySelector('.personalisation__counter')) return;

      const header = document.createElement('div');
      header.className = 'personalisation__field-header';

      const counter = document.createElement('span');
      counter.className = 'personalisation__counter';
      counter.setAttribute('aria-hidden', 'true');

      label.replaceWith(header);
      header.appendChild(label);
      header.appendChild(counter);

      this._updateCounter(input);
    }

    _updateCounter(input) {
      const counter = input
        .closest('.product-form__line-item-property')
        ?.querySelector('.personalisation__counter');

      if (counter) {
        counter.textContent = `${input.value.length}/${input.maxLength}`;
      }
    }

    /* ---------------------------------------------------------------- *
     * Option selection
     * ---------------------------------------------------------------- */

    /** Engraving: a single checkbox flipping the option between its on and off values. */
    _setupToggles() {
      this.querySelectorAll('[data-personalisation-toggle]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const card = checkbox.closest('[data-personalisation-card]');
          const valueId = checkbox.checked
            ? card.getAttribute('data-on-value-id')
            : card.getAttribute('data-off-value-id');

          this._selectOptionValue(card, valueId);
        });
      });
    }

    /**
     * Greeting: one checkbox per real option value, behaving as a mutually exclusive pair.
     * Ticking one clears the other; unticking both falls back to the "no message" value.
     */
    _setupChoices() {
      this.querySelectorAll('[data-personalisation-choice]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const card = checkbox.closest('[data-personalisation-card]');
          const group = card.getAttribute('data-personalisation-card');

          if (checkbox.checked) {
            card
              .querySelectorAll(`[data-personalisation-choice="${group}"]`)
              .forEach((other) => {
                if (other !== checkbox) other.checked = false;
              });

            this._selectOptionValue(card, checkbox.getAttribute('data-value-id'));
          } else {
            this._selectOptionValue(card, card.getAttribute('data-off-value-id'));
          }

          // Reflect the photo field straight away rather than waiting for the section fetch.
          this._syncPhotoUpload();
        });
      });
    }

    /**
     * Switching to a colour that cannot be engraved while the add-on is on would ask
     * <variant-picker> for a combination no variant has. Turn the add-on off first.
     *
     * This listener sits on the component, which the colour radio's change event passes through
     * on its way to the picker's document.body delegate, so the correction always lands before
     * the picker reads the option inputs.
     */
    _setupColourGuard() {
      this.addEventListener('change', (event) => {
        const colourGroup = this.querySelector('[data-personalisation-group="colour"]');

        if (!colourGroup || !colourGroup.contains(event.target) || !event.target.checked) return;

        const colour = event.target.getAttribute('data-value-name');
        const colourIndex = this._optionIndex(colourGroup);
        const current = this._currentSelection();

        if (!colour || colourIndex === null || !current) return;

        const selection = current.slice();
        selection[colourIndex] = colour;

        this.querySelectorAll('[data-personalisation-card][data-off-value-id]').forEach((card) => {
          const index = this._optionIndex(card);
          const onValue = card.getAttribute('data-on-value-name');
          const toggle = card.querySelector('[data-personalisation-toggle]');

          if (index === null || !onValue || !toggle?.checked) return;
          if (this._combinationExists(selection, index, onValue)) return;

          toggle.checked = false;

          const input = card.querySelector('input[data-personalisation-input]');

          // Value only, no dispatch: the picker reads the hidden inputs out of the DOM while
          // handling this same event, so a second event would only buy a second section fetch.
          if (input) {
            input.value = card.getAttribute('data-off-value-id');
          }
        });
      });
    }

    /**
     * Write the option value id into the card's hidden option input and let <variant-picker>
     * take it from there. The event must bubble to reach its document.body delegate.
     */
    _selectOptionValue(card, valueId) {
      const input = card.querySelector('input[data-personalisation-input]');

      if (!input || !valueId) return;

      input.value = valueId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------------------------------------------------------------- *
     * State sync
     * ---------------------------------------------------------------- */

    _onVariantChanged(event) {
      const variant = event.detail?.variant;

      if (!variant || !variant.options) return;
      if (event.detail.formId && this.formElement && event.detail.formId !== this.formElement.id) return;

      this._syncColour(variant);
      this._syncCards(variant);
      this._syncPhotoUpload(variant.options[this._greetingIndex()] || null);
      this._syncAvailability(variant);
      this._syncGalleryMedia(variant);
      this._syncFieldPreviews();
    }

    /**
     * Put both previews back to their placeholders when an add-on is switched off.
     *
     * setupVariant() in custom.js empties the field on deactivate, but assigning .value in script
     * fires no input event — so without this the previews keep showing whatever was last typed
     * after the customer has unticked the option. Runs after custom.js's own variant:changed
     * listener, which was registered first at parse time, so the fields are already cleared by the
     * time this reads them.
     */
    _syncFieldPreviews() {
      if (this.engravingInput) {
        this._updateCounter(this.engravingInput);
        this._updatePill(this.engravingInput.value);
      }

      if (this.greetingInput) {
        const value = this.greetingInput.value || '';

        // Rebase the three-line guard too, or it would measure the next keystroke against a
        // message that is no longer in the field.
        this.lastAcceptedMessage = value;
        this._updateCounter(this.greetingInput);
        this._renderGreetingMessage(value);
      }
    }

    /* ---------------------------------------------------------------- *
     * Gallery media
     *
     * Show the general photography plus only the selected variant's image. Two rules decide what
     * is hidden: media assigned to another variant, and Focal's own alt-text grouping
     * (…#Colour_Rose), which some products still carry. Both are applied here with one class, and
     * the live carousel is updated in place through Flickity's reloadCells(), so a swatch change
     * never destroys and recreates it — that rebuild is what flashed the main image.
     * ---------------------------------------------------------------- */

    /**
     * Deliberately NOT Focal's own `is-filtered`, and any `is-filtered` Liquid rendered is
     * converted to ours. ProductMedia._onVariantChanged reads `.is-filtered[data-media-id]` to
     * decide whether to destroy and rebuild the carousel. With none present it always takes its
     * other branch, which just selects the variant's image — so Focal never rebuilds behind us,
     * and the alt-text rule it would have applied is applied here instead.
     */
    _syncGalleryMedia(variant) {
      const media = document.querySelector('product-media');

      if (!media) return;

      // Every media that belongs to some variant. Anything outside this set is general
      // photography and stays visible throughout.
      const variantMedia = new Set(this.variants.map((entry) => entry.m).filter(Boolean));
      const selectedVariant = variant || this.closest('variant-picker')?.selectedVariant || null;
      const current = selectedVariant?.featured_media?.id || null;
      const alts = this._galleryAlts(media);

      if (!variantMedia.size && !Array.from(alts.values()).some((alt) => alt.includes('#'))) return;

      const shown = [];
      const hidden = [];

      media.querySelectorAll('[data-media-id]').forEach((element) => {
        const id = parseInt(element.getAttribute('data-media-id'), 10);
        const hide =
          id !== current &&
          (variantMedia.has(id) || this._isAltGroupHidden(alts.get(id), selectedVariant));

        element.classList.remove('is-filtered');
        element.classList.toggle('is-variant-hidden', hide);
        (hide ? hidden : shown).push(element);
      });

      // Zero visible cells leaves Flickity with selectedElement undefined, which several call
      // sites dereference without a guard. Back everything out rather than ship a broken gallery.
      if (!shown.length) {
        hidden.forEach((element) => element.classList.remove('is-variant-hidden'));
        return;
      }

      // Flickity reads initialIndex as the selector '.is-initial-selected', the theme's CSS shows
      // only '.is-selected' before activation, and PhotoSwipe measures '.is-selected' for its
      // zoom origin — so all three have to point at a visible item.
      const target = current
        ? media.querySelector(`.product__media-item[data-media-id="${current}"]:not(.is-variant-hidden)`)
        : null;
      const selected = target || media.querySelector('.product__media-item:not(.is-variant-hidden)');

      if (selected) {
        media.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.toggle('is-selected', item === selected);
          item.classList.toggle('is-initial-selected', item === selected);
        });
      }

      this._refreshCarousel(media, selected);
    }

    /** Alt text per media id, read from the rendered images (dots carry no image of their own). */
    _galleryAlts(media) {
      const alts = new Map();

      media.querySelectorAll('.product__media-item[data-media-id]').forEach((item) => {
        const alt = item.querySelector('img[alt]')?.getAttribute('alt') || '';
        alts.set(parseInt(item.getAttribute('data-media-id'), 10), alt);
      });

      return alts;
    }

    /**
     * Focal's grouping rule, as in ProductMedia._onVariantChanged: an alt ending "#Colour_Rose"
     * belongs to the Colour option's "Rose" value and hides whenever another value is selected.
     */
    _isAltGroupHidden(alt, variant) {
      if (!alt || !alt.includes('#') || !variant?.options) return false;

      const [name, value = ''] = alt.split('#').pop().split('_');
      const position = this.optionNames.findIndex((option) => option.toLowerCase() === name.toLowerCase());

      if (position === -1 || variant.options[position] == null) return false;

      return String(variant.options[position]).toLowerCase() !== value.trim().toLowerCase();
    }

    /**
     * Only touch Flickity once it has actually activated. On first load our classes are applied
     * synchronously from connectedCallback, while _createFlickity is still awaiting the library
     * load and an IntersectionObserver tick — so the hidden items are excluded by cellSelector
     * from the start and there is nothing to refresh.
     */
    _carouselIsLive(carousel) {
      return carousel.classList.contains('flickity-enabled');
    }

    /**
     * Swap the cells of the live carousel without rebuilding it. Flickity only moves matching
     * cells into its slider on activation, so every item goes back into the slider in its original
     * order first; reloadCells() then re-reads them through the cellSelector, which skips
     * .is-variant-hidden. The change/settle events are re-fired because Focal keeps the
     * thumbnails' current state and the zoom button in step from them, and the selected index
     * may not have changed even though the element it points at has.
     */
    async _refreshCarousel(media, selected) {
      const carousel = media.querySelector('flickity-carousel');

      // A single-media product never instantiates Flickity.
      if (!carousel || !carousel.flickityInstance) return;
      if (!this._carouselIsLive(carousel)) return;

      try {
        const flickity = await carousel.flickityInstance;

        if (!flickity?.isActive) return;

        const previous = flickity.cells;

        Array.from(carousel.querySelectorAll('.product__media-item'))
          .sort((a, b) => parseInt(a.getAttribute('data-original-position'), 10) - parseInt(b.getAttribute('data-original-position'), 10))
          .forEach((item) => flickity.slider.appendChild(item));

        flickity.reloadCells();

        // Clear Flickity's inline positioning from items that are no longer cells.
        previous
          .filter((cell) => !flickity.cells.some((next) => next.element === cell.element))
          .forEach((cell) => cell.destroy());

        flickity.resize();

        const index = Math.max(0, flickity.cells.findIndex((cell) => cell.element === selected));

        flickity.select(index, false, true);
        flickity.dispatchEvent('change', null, [index]);
        flickity.dispatchEvent('settle', null, [index]);
      } catch (error) {
        console.warn('[FFS] personalisation: gallery refresh failed', error);
      }
    }

    /**
     * ProductImageZoom builds its slide list with a hardcoded ':not(.is-filtered)', so hidden
     * variant images would still show in the PhotoSwipe view — the one place the carousel's
     * cellSelector cannot reach. Mask them with that class for the duration of the call only; it
     * reads the DOM synchronously at the top, so the window is a single microtask.
     */
    _patchZoom() {
      const ZoomElement = window.customElements.get('product-image-zoom');

      if (!ZoomElement || ZoomElement.prototype.ffsVariantMediaPatched) return;

      const original = ZoomElement.prototype._buildItems;

      if (typeof original !== 'function') return;

      ZoomElement.prototype._buildItems = function () {
        const masked = Array.from(document.querySelectorAll('.product__media-item.is-variant-hidden'));

        masked.forEach((element) => element.classList.add('is-filtered'));

        try {
          return original.apply(this, arguments);
        } finally {
          masked.forEach((element) => element.classList.remove('is-filtered'));
        }
      };

      ZoomElement.prototype.ffsVariantMediaPatched = true;
    }

    _syncColour(variant) {
      const group = this.querySelector('[data-personalisation-group="colour"]');

      if (!group) return;

      const selected = variant.options[this._optionIndex(group)];
      const label = group.querySelector('[data-personalisation-colour-value]');

      if (label && selected) {
        label.textContent = selected;
      }

      this._syncPillFinish(selected);
    }

    /** Rose gold for the colours named in the setting, chrome for everything else. */
    _syncPillFinish(colourName) {
      if (!this.pill || !colourName) return;

      const isRose = this.roseFinishValues.includes(colourName.trim().toLowerCase());

      this.pill.classList.toggle('personalisation__pill--rose', isRose);
      this.pill.classList.toggle('personalisation__pill--chrome', !isRose);
    }

    _syncCards(variant) {
      this.querySelectorAll('[data-personalisation-card]').forEach((card) => {
        const selected = variant.options[this._optionIndex(card)];
        const hiddenInput = card.querySelector('input[data-personalisation-input]');
        const offValue = card.getAttribute('data-off-value-name');
        const isActive = Boolean(selected) && selected !== offValue;

        card.classList.toggle('is-active', isActive);

        const toggle = card.querySelector('[data-personalisation-toggle]');

        if (toggle) {
          toggle.checked = isActive;
        }

        card.querySelectorAll('[data-personalisation-choice]').forEach((choice) => {
          choice.checked = choice.getAttribute('data-value-name') === selected;
        });

        // Keep the hidden input honest — the variant may have been changed by something other
        // than this component (a ?variant= link, the recharge widget, a combined listing).
        if (hiddenInput && selected) {
          let valueId = null;

          if (selected === card.getAttribute('data-on-value-name')) {
            valueId = card.getAttribute('data-on-value-id');
          } else if (selected === offValue) {
            valueId = card.getAttribute('data-off-value-id');
          } else {
            const match = Array.from(card.querySelectorAll('input[data-value-id]')).find(
              (element) => element.getAttribute('data-value-name') === selected
            );

            valueId = match?.getAttribute('data-value-id') || null;
          }

          if (valueId) {
            hiddenInput.value = valueId;
          }
        }
      });
    }

    /**
     * Grey out option values that no variant offers alongside the rest of the current
     * selection. Done client side because this element is never re-rendered by the server.
     */
    _syncAvailability(variant) {
      if (!this.variants.length) return;

      const selection = variant?.options || this._currentSelection();

      if (!selection) return;

      this.querySelectorAll('[data-personalisation-group], [data-personalisation-card]').forEach((group) => {
        const index = this._optionIndex(group);

        if (index === null) return;

        group.querySelectorAll('[data-availability-item]').forEach((element) => {
          const candidate = selection.slice();
          candidate[index] = element.getAttribute('data-value-name');

          const available = this.variants.some(
            (entry) => entry.a && entry.o.every((option, position) => option === candidate[position])
          );

          element.classList.toggle('is-disabled', !available);

          const input = element.querySelector('input');

          if (input) {
            input.disabled = !available && !input.checked;
          }
        });
      });

      this._syncCardUpgradeState(selection);
    }

    /**
     * Some colours have no engravable variant at all (the Razor Kit's non-premium handles). The
     * card still shows, but its checkbox gives way to the upgrade line.
     */
    _syncCardUpgradeState(selection) {
      this.querySelectorAll('[data-personalisation-card][data-off-value-id]').forEach((card) => {
        const index = this._optionIndex(card);
        const onValue = card.getAttribute('data-on-value-name');

        if (index === null || !onValue) return;

        card.classList.toggle('is-unavailable', !this._combinationExists(selection, index, onValue));
      });
    }

    /**
     * Does a variant exist for this selection with one option swapped? Asks about existence, not
     * stock — deliberately unlike the check above, which asks whether it can be ordered.
     */
    _combinationExists(selection, index, valueName) {
      const candidate = selection.slice();
      candidate[index] = valueName;

      return this.variants.some((entry) => entry.o.every((option, position) => option === candidate[position]));
    }

    _currentSelection() {
      const variant = this.closest('variant-picker')?.selectedVariant;

      return variant?.options || null;
    }

    _greetingIndex() {
      const card = this.querySelector('[data-personalisation-card="greeting"]');

      return card ? this._optionIndex(card) : null;
    }

    _optionIndex(element) {
      const position = parseInt(element.getAttribute('data-option-position'), 10);

      return position > 0 ? position - 1 : null;
    }

    _updatePill(value) {
      if (!this.pillText) return;

      const placeholder = this.pill?.getAttribute('data-placeholder') || '';

      this.pillText.textContent = value?.trim() ? value : placeholder;
    }
  }

  window.customElements.define('personalisation-selectors', PersonalisationSelectors);
}
